import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/auth';
import useSmartAuth from '../../hooks/useSmartAuth';
import { UI_CONFIG } from '../../config/authConfig';
import './styles.css';

/**
 * Componente discreto para mostrar status da sessão
 * Aparece apenas quando necessário, sem interromper a experiência
 */
const SessionStatusIndicator = () => {
  const { signed } = useAuth();
  const { sessionHealth, shouldShowWarning, isSessionHealthy } = useSmartAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!signed || !UI_CONFIG.VISUAL_FEEDBACK.SHOW_INACTIVITY_WARNING) {
      setIsVisible(false);
      return;
    }

    // Mostrar aviso apenas se necessário
    if (shouldShowWarning && isSessionHealthy) {
      const timeLeft = Math.ceil((sessionHealth.expiresIn - Date.now()) / (1000 * 60));
      setMessage(`Sua sessão expira em ${timeLeft} minutos`);
      setIsVisible(true);

      // Auto-esconder após 10 segundos
      const hideTimeout = setTimeout(() => setIsVisible(false), 10000);
      return () => clearTimeout(hideTimeout);
    } else if (!isSessionHealthy && sessionHealth.renewalAttempts > 0) {
      setMessage('Verificando conexão...');
      setIsVisible(true);

      // Auto-esconder após 5 segundos
      const hideTimeout = setTimeout(() => setIsVisible(false), 5000);
      return () => clearTimeout(hideTimeout);
    } else {
      setIsVisible(false);
    }
  }, [signed, shouldShowWarning, isSessionHealthy, sessionHealth]);

  if (!isVisible) return null;

  return (
    <div className="session-status-indicator">
      <div className="session-status-content">
        <div className="session-status-icon">
          {isSessionHealthy ? (
            <svg className="status-icon warning" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="status-icon info animate-spin" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <span className="session-status-text">{message}</span>
        <button 
          onClick={() => setIsVisible(false)}
          className="session-status-close"
          aria-label="Fechar notificação"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SessionStatusIndicator;
